/**
 * Lifetime play statistics over StorageLike (so writes flow through the
 * synced-storage decorator and `?debug` sandboxing). losses/waves are synced
 * keys (game_saves.losses / .waves); lastBag is DELIBERATELY device-local —
 * losing it on a device switch only costs a Doubles attempt (spec decision).
 * The quests' lifetime counters live as one JSON object under `hambi.stats`
 * (synced: game_saves.stats; spec 2026-09-08-quests-and-potions-design.md).
 * Spec: docs/superpowers/specs/2026-08-26-achievements-batch2-phantom-design.md
 */
import type { StorageLike } from '../account/saveSync';
import {
  bumpStat,
  parseStats,
  STATS_KEY,
  type LifetimeStats,
  type StatField,
} from '../config/lifetimeStats';

export const LOSSES_KEY = 'hambi.losses';
export const WAVES_KEY = 'hambi.waves';
export const LAST_BAG_KEY = 'hambi.lastBag';

export class StatsService {
  constructor(private readonly storage: StorageLike) {}

  private read(key: string): number {
    const n = parseInt(this.storage.getItem(key) ?? '', 10);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  }

  private bump(key: string): number {
    const next = this.read(key) + 1;
    this.storage.setItem(key, String(next));
    return next;
  }

  /** Total campaign defeats ("The Picnic Fell!"). */
  losses(): number {
    return this.read(LOSSES_KEY);
  }

  /** Total campaign waves cleared. */
  waves(): number {
    return this.read(WAVES_KEY);
  }

  /** Record one loss; returns the new total. */
  incLosses(): number {
    return this.bump(LOSSES_KEY);
  }

  /** Record one cleared wave; returns the new total. */
  incWaves(): number {
    return this.bump(WAVES_KEY);
  }

  /** The tower the previous takeout bag rolled (Doubles detection). */
  lastBag(): string | null {
    return this.storage.getItem(LAST_BAG_KEY);
  }

  setLastBag(tower: string): void {
    this.storage.setItem(LAST_BAG_KEY, tower);
  }

  /** The lifetime counters the quests measure (see config/lifetimeStats). */
  stats(): LifetimeStats {
    return parseStats(this.storage.getItem(STATS_KEY));
  }

  /** Add `n` to one lifetime counter; returns the new stats. */
  inc(field: StatField, n = 1): LifetimeStats {
    return bumpStat(this.storage, field, n);
  }
}
